import type {
  ConversationId,
  ConversationThreadId,
  ShopperAiIntegrationId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { IntegrationWithChannelDataResponse } from "@redotech/effect-api-shared/models/shopper-ai/integration/integration";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RedoModal,
  type RedoModalButtonProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateAndPublishConversation,
  useCreateDraftConversation,
  useSaveConversationDraft,
} from "../hooks/use-create-conversation";
import { useIntegrations } from "../hooks/use-integrations";
import { EmailToolbar } from "./message-editor/email-toolbar";
import { MessageEditor } from "./message-editor/message-editor";
import { useMessageEditor } from "./message-editor/use-message-editor";

type EmailIntegration = Extract<
  IntegrationWithChannelDataResponse,
  { integrationType: "gmail" | "outlook" }
>;

export interface ExistingDraftData {
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  integrationId: ShopperAiIntegrationId;
  customerEmail?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  /** Plate JSON string for pre-filling the editor */
  draftContent?: string;
}

function parseEmails(raw: string): { email: string; name: null }[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((email) => ({ email, name: null }));
}

const CHANNEL_OPTIONS: RedoListItem<string>[] = [
  { type: "text", id: "email", value: "email", text: "Email" },
];

function integrationToListItem(
  integration: EmailIntegration,
): RedoListItem<EmailIntegration> {
  const email = integration.channelData?.email ?? integration.integrationType;
  return {
    type: "text",
    id: String(integration.id),
    value: integration,
    text: email,
  };
}

export const NewConversationModal = memo(function NewConversationModal({
  open,
  setOpen,
  onCreated,
  existingDraft,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCreated: (conversationId: string) => void;
  existingDraft?: ExistingDraftData;
}) {
  const { createDraftConversation, isCreatingDraft } =
    useCreateDraftConversation();
  const { saveConversationDraft, isSavingDraft } = useSaveConversationDraft();
  const { createAndPublish, isSubmitting } = useCreateAndPublishConversation();
  const { data: allIntegrations, isLoading: isLoadingIntegrations } =
    useIntegrations();

  const editorState = useMessageEditor("email");

  const integrationOptions = useMemo<RedoListItem<EmailIntegration>[]>(() => {
    const emailIntegrations = (allIntegrations ?? []).filter(
      (i): i is EmailIntegration =>
        i.integrationType === "gmail" || i.integrationType === "outlook",
    );
    return emailIntegrations.map(integrationToListItem);
  }, [allIntegrations]);

  const [userSelectedIntegration, setUserSelectedIntegration] = useState<
    RedoListItem<EmailIntegration> | undefined
  >(undefined);
  const [toEmails, setToEmails] = useState(existingDraft?.customerEmail ?? "");
  const [ccEmails, setCcEmails] = useState(existingDraft?.cc ?? "");
  const [bccEmails, setBccEmails] = useState(existingDraft?.bcc ?? "");
  const [subject, setSubject] = useState(existingDraft?.subject ?? "");
  const [hasEdits, setHasEdits] = useState(false);
  // Tracks whether the editor's initial content has been set, so we can
  // distinguish initialization-triggered onChange events from user edits.
  const editorInitializedRef = useRef(!existingDraft?.draftContent);

  // Derive selected integration: user override > draft match > first available
  const effectiveSelected = useMemo(() => {
    if (userSelectedIntegration) return userSelectedIntegration;
    if (existingDraft?.integrationId && integrationOptions.length > 0) {
      const match = integrationOptions.find(
        (opt) => String(opt.value.id) === String(existingDraft.integrationId),
      );
      if (match) return match;
    }
    return integrationOptions[0];
  }, [
    userSelectedIntegration,
    existingDraft?.integrationId,
    integrationOptions,
  ]);

  // Initialize form fields when draft data arrives asynchronously
  useEffect(() => {
    if (!existingDraft) return;
    if (existingDraft.cc !== undefined) setCcEmails(existingDraft.cc);
    if (existingDraft.bcc !== undefined) setBccEmails(existingDraft.bcc);
    if (existingDraft.draftContent && editorState.editor) {
      editorInitializedRef.current = false;
      try {
        editorState.setValue(JSON.parse(existingDraft.draftContent));
      } catch {
        editorInitializedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally granular deps to avoid re-running on unrelated draft changes
  }, [
    existingDraft?.cc,
    existingDraft?.bcc,
    existingDraft?.draftContent,
    editorState,
  ]);

  // Save as Draft only needs an integration selected; customer is optional
  const saveAsDraftDisabled =
    isCreatingDraft || isLoadingIntegrations || !effectiveSelected;

  // Create conversation (send) requires integration + at least one To address + subject
  const submitDisabled =
    isLoadingIntegrations ||
    !effectiveSelected ||
    toEmails.trim().length === 0 ||
    subject.trim().length === 0;

  const handleClose = () => {
    setOpen(false);
    setToEmails("");
    setCcEmails("");
    setBccEmails("");
    setSubject("");
    editorState.clearContent();
    setUserSelectedIntegration(undefined);
    setHasEdits(false);
    editorInitializedRef.current = false;
  };

  const handleSaveAsDraft = async () => {
    if (!effectiveSelected) return;

    const plainText = editorState.getPlainText();
    const draftContent = plainText.trim()
      ? JSON.stringify(editorState.editor?.children ?? [])
      : undefined;

    const primaryTo = parseEmails(toEmails)[0]?.email ?? toEmails.trim();
    const cc = parseEmails(ccEmails);
    const bcc = parseEmails(bccEmails);

    const result = await createDraftConversation({
      integrationId: effectiveSelected.value.id as ShopperAiIntegrationId,
      customerEmail: primaryTo || undefined,
      subject: subject.trim() || undefined,
      draftContent,
      cc: cc.length ? cc : undefined,
      bcc: bcc.length ? bcc : undefined,
    });

    handleClose();
    onCreated(result.conversationId);
  };

  const handleSaveChanges = async () => {
    if (!existingDraft) return;
    const cc = parseEmails(ccEmails);
    const bcc = parseEmails(bccEmails);
    await saveConversationDraft({
      conversationId: existingDraft.conversationId,
      threadId: existingDraft.threadId,
      content: editorState.getSerializedValue(),
      cc: cc.length ? cc : undefined,
      bcc: bcc.length ? bcc : undefined,
    });
    setHasEdits(false);
  };

  const handleSubmit = async () => {
    if (!effectiveSelected) return;

    const content = await editorState.getHtml();
    const to = parseEmails(toEmails);
    const cc = parseEmails(ccEmails);
    const bcc = parseEmails(bccEmails);
    const primaryEmail = to[0]?.email ?? toEmails.trim();
    const capturedSubject = subject.trim();
    const capturedExistingDraft = existingDraft;
    const capturedIntegrationId = effectiveSelected.value
      .id as ShopperAiIntegrationId;
    const capturedIntegrationType = effectiveSelected.value.integrationType;

    void createAndPublish({
      integrationType: capturedIntegrationType,
      integrationId: capturedIntegrationId,
      existingConversationId: capturedExistingDraft?.conversationId,
      primaryEmail,
      subject: capturedSubject,
      content,
      to,
      cc: cc.length ? cc : undefined,
      bcc: bcc.length ? bcc : undefined,
      onCreated,
    });
  };

  const primaryButton: RedoModalButtonProps = {
    size: "sm",
    text: "Create ticket",
    onClick: handleSubmit,
    disabled: submitDisabled,
    pending: isSubmitting,
  };

  const secondaryButton: RedoModalButtonProps | undefined = existingDraft
    ? {
        text: "Save changes",
        onClick: handleSaveChanges,
        disabled: !hasEdits || isSavingDraft,
        size: "sm",
      }
    : {
        text: "Save as draft",
        onClick: handleSaveAsDraft,
        disabled: saveAsDraftDisabled,
        pending: isCreatingDraft,
        size: "sm",
      };

  return (
    <RedoModal
      buttonPlacement="tight"
      isOpen={open}
      onModalCloseRequested={handleClose}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      title="New conversation"
    >
      <Flex dir="column" gap="xl">
        <RedoSingleSelectDropdownInput
          label="Channel"
          options={CHANNEL_OPTIONS}
          optionSelected={() => {}}
          selectedItem={CHANNEL_OPTIONS[0]}
          size="xs"
        />
        <RedoSingleSelectDropdownInput
          label="Send from"
          options={integrationOptions}
          optionSelected={({ item }) => setUserSelectedIntegration(item)}
          selectedItem={effectiveSelected}
          size="xs"
        />
        <RedoTextInput
          label="To"
          placeholder="customer@example.com"
          setValue={(v) => {
            setToEmails(v);
            if (existingDraft) setHasEdits(true);
          }}
          size="sm"
          value={toEmails}
        />
        <RedoTextInput
          label="CC"
          placeholder="cc@example.com"
          setValue={(v) => {
            setCcEmails(v);
            if (existingDraft) setHasEdits(true);
          }}
          size="sm"
          value={ccEmails}
        />
        <RedoTextInput
          label="BCC"
          placeholder="bcc@example.com"
          setValue={(v) => {
            setBccEmails(v);
            if (existingDraft) setHasEdits(true);
          }}
          size="sm"
          value={bccEmails}
        />
        <RedoTextInput
          label="Subject"
          placeholder="What's this conversation about?"
          setValue={(v) => {
            setSubject(v);
            if (existingDraft) setHasEdits(true);
          }}
          size="sm"
          value={subject}
        />
        <MessageEditor
          className="[&_[contenteditable]]:min-h-[120px]"
          editorState={editorState}
          onChange={() => {
            if (editorInitializedRef.current) {
              if (existingDraft) setHasEdits(true);
            } else {
              editorInitializedRef.current = true;
            }
          }}
          placeholder="Write your message here..."
          toolbar={<EmailToolbar />}
        />
      </Flex>
    </RedoModal>
  );
});
